import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { NavbarComponent } from './navbar/navbar.component';
import { ErrorDialogComponent } from './error-dialog/error-dialog.component';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
	MsalService,
	MsalBroadcastService,
	MSAL_GUARD_CONFIG,
	MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
	InteractionStatus,
	RedirectRequest,
	EventMessage,
	EventType,
} from '@azure/msal-browser';

@Component({
	selector: 'app-root',
	imports: [RouterOutlet, NavbarComponent, ErrorDialogComponent],
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
	private translateService = inject(TranslateService);
	private langChangeSubscription?: Subscription;
	private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
	private authService = inject(MsalService);
	private msalBroadcastService = inject(MsalBroadcastService);

	title = 'iterlex-frontend';
	loginDisplay = false;
	private readonly _destroying$ = new Subject<void>();

	ngOnInit(): void {
		this.setHtmlLangAttribute(
			this.translateService.getCurrentLang() ||
				this.translateService.getFallbackLang() ||
				'en',
		);
		this.langChangeSubscription =
			this.translateService.onLangChange.subscribe(
				(event: LangChangeEvent) => {
					this.setHtmlLangAttribute(event.lang);
				},
			);
		this.authService.handleRedirectObservable().subscribe();

		this.msalBroadcastService.msalSubject$
			.pipe(
				filter(
					(msg: EventMessage) =>
						msg.eventType === EventType.LOGIN_SUCCESS ||
						msg.eventType === EventType.LOGOUT_SUCCESS,
				),
			)
			.subscribe((result: EventMessage) => {
				console.log(result);
				if (this.authService.instance.getAllAccounts().length === 0) {
					window.location.pathname = '/';
				} else {
					this.setLoginDisplay();
				}
			});

		this.msalBroadcastService.inProgress$
			.pipe(
				filter(
					(status: InteractionStatus) =>
						status === InteractionStatus.None,
				),
				takeUntil(this._destroying$),
			)
			.subscribe(() => {
				this.setLoginDisplay();
				this.checkAndSetActiveAccount();
			});
	}
	setLoginDisplay() {
		this.loginDisplay =
			this.authService.instance.getAllAccounts().length > 0;
	}

	checkAndSetActiveAccount() {
		/**
		 * If no active account set but there are accounts signed in, sets first account to active account
		 * To use active account set here, subscribe to inProgress$ first in your component
		 * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
		 */
		const activeAccount = this.authService.instance.getActiveAccount();

		if (
			!activeAccount &&
			this.authService.instance.getAllAccounts().length > 0
		) {
			const accounts = this.authService.instance.getAllAccounts();
			this.authService.instance.setActiveAccount(accounts[0]);
		}
	}

	login() {
		if (this.msalGuardConfig.authRequest) {
			this.authService.loginRedirect({
				...this.msalGuardConfig.authRequest,
			} as RedirectRequest);
		} else {
			this.authService.loginRedirect();
		}
	}

	logout() {
		this.authService.logoutRedirect();
	}

	private setHtmlLangAttribute(lang: string): void {
		if (lang && typeof document !== 'undefined') {
			document.documentElement.setAttribute('lang', lang);
		}
	}

	ngOnDestroy(): void {
		this.langChangeSubscription?.unsubscribe();
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}
}
